import { TERMINAL_DATA } from '../constants/terminalConstants';
import { translations } from '../i18n/translations';
import {
  formatSezione,
  getSharedTerminalGroupEndpoints,
  normalizeMultipolarPole,
  getStandardCableSideSiglature,
  normalizeStandardCable
} from './cableLogic';
import { getColorsForLanguage, isCanonicalColor, normalizeColorToCanonical } from './colorUtils';
import { withEW3035Settings } from '../machines/EW-3035+L';
import { normalizeSectionForComparison } from './sectionNormalization';
import { evaluateTerminalSectionCompatibility } from './terminalCompatibility';

const getTranslation = (lingua, key, params = {}) => {
  const lang = (lingua && translations[lingua]) ? lingua : 'it';
  const group = translations[lang]?.validation || translations['it'].validation;
  let text = group[key] || key;
  Object.keys(params).forEach(k => {
    text = text.replace(`{${k}}`, params[k]);
  });
  return text;
};

export const validateTerminal = (terminalCode, lingua, terminali = TERMINAL_DATA) => {
  if (!terminalCode || terminalCode.trim() === '') {
    return { isValid: false, error: getTranslation(lingua, 'emptyTerminal') };
  }

  if (terminalCode.startsWith('SP') && terminalCode.length > 2) {
    const match = terminalCode.match(/^SP(\d+)\/(\d+)$/);
    if (match) {
      const length = parseInt(match[1]);
      const peel = parseInt(match[2]);
      if (isNaN(length) || length < 0 || length > 100) {
        return { isValid: false, error: getTranslation(lingua, 'invalidSPLength', { length: match[1] }) };
      }
      if (isNaN(peel) || peel < 0 || peel > 50) {
        return { isValid: false, error: getTranslation(lingua, 'invalidSPPeel', { peel: match[2] }) };
      }
      return { isValid: true, error: null };
    }

    const lengthPart = terminalCode.substring(2);
    const length = parseInt(lengthPart);
    if (isNaN(length) || length <= 0 || length > 100) {
      return { isValid: false, error: getTranslation(lingua, 'invalidSPLength', { length: lengthPart }) };
    }
    return { isValid: true, error: null };
  }

  if (!(terminali || TERMINAL_DATA)[terminalCode]) {
    return { isValid: false, error: getTranslation(lingua, 'unknownTerminal', { terminal: terminalCode }) };
  }

  return { isValid: true, error: null };
};

export const validateColor = (color, lingua) => {
  if (!color || color.trim() === '') {
    return { isValid: false, error: getTranslation(lingua, 'emptyColor') };
  }

  const normalizedColor = normalizeColorToCanonical(color);
  const availableColors = getColorsForLanguage();
  if (!isCanonicalColor(normalizedColor) || !availableColors.includes(normalizedColor)) {
    return { isValid: false, error: getTranslation(lingua, 'unknownColor', { color }) };
  }

  return { isValid: true, error: null };
};

export const checkIfCableIsDuplicate = (targetCable, cables) => {
  const duplicateIndexes = [];

  cables.forEach((cable, index) => {
    const isSameCable = (
      cable === targetCable ||
      (cable.originalIndex !== undefined &&
        targetCable.originalIndex !== undefined &&
        cable.originalIndex === targetCable.originalIndex)
    );

    if (isSameCable) {
      duplicateIndexes.push(index);
      return;
    }

    const fieldComparisons = {
      siglaturaA: cable.siglaturaA === targetCable.siglaturaA,
      siglaturaB: cable.siglaturaB === targetCable.siglaturaB,
      useSeparateSiglature: cable.useSeparateSiglature === targetCable.useSeparateSiglature,
      colore: cable.colore === targetCable.colore,
      sezione: cable.sezione === targetCable.sezione,
      lunghezza: cable.lunghezza === targetCable.lunghezza,
      terminaleA: cable.terminaleA === targetCable.terminaleA,
      terminaleB: cable.terminaleB === targetCable.terminaleB,
      collegamentoA: cable.collegamentoA === targetCable.collegamentoA,
      collegamentoB: cable.collegamentoB === targetCable.collegamentoB,
      gruppo: cable.gruppo === targetCable.gruppo,
      guaina: cable.guaina === targetCable.guaina
    };

    const areIdentical = Object.values(fieldComparisons).every(Boolean);

    if (areIdentical) {
      duplicateIndexes.push(index);
    }
  });

  return {
    isDuplicate: duplicateIndexes.length > 1,
    duplicateIndexes
  };
};

const normalizeSectionForValidation = (sectionValue, useAWG = false) => {
  return normalizeSectionForComparison(sectionValue, useAWG);
};

const getSectionsForGroupedTerminal = ({ cables, targetEndpoint, settings, useAWG = false }) => {
  if (!targetEndpoint?.connection || !Array.isArray(cables) || cables.length === 0) {
    return [];
  }

  return getSharedTerminalGroupEndpoints({
    targetEndpoint,
    cables,
    settings
  })
    .map(endpoint => normalizeSectionForValidation(endpoint.sezione, useAWG))
    .filter(s => !isNaN(s));
};

export const validateCable = (cable, cables, lingua, useAWG = false, settings = {}) => {
  const effectiveSettings = withEW3035Settings(settings);
  const terminalData = effectiveSettings.terminal_data || TERMINAL_DATA;
  const issues = [];

  const validateSide = ({
    terminalCode,
    collegamento,
    siglatura,
    sezioneValue,
    side,
    poloIndex = null
  }) => {
    if (!terminalCode) return;

    const config = terminalData[terminalCode];
    if (!config || (config.minSection == null && config.maxSection == null)) return;

    const originalSezione = sezioneValue;
    const normalizedSezione = normalizeSectionForValidation(sezioneValue, useAWG);
    let sum = normalizedSezione;
    let isGrouped = false;

    if (collegamento && Array.isArray(cables) && config.allowsMultiple !== false) {
      const groupedSections = getSectionsForGroupedTerminal({
        cables,
        settings: effectiveSettings,
        targetEndpoint: {
          cable,
          side,
          poloIndex,
          connection: collegamento,
          terminal: terminalCode,
          sezione: sezioneValue,
          siglatura
        },
        useAWG
      });

      if (groupedSections.length > 1) {
        sum = Math.round(groupedSections.reduce((a, b) => a + b, 0) * 100) / 100;
        isGrouped = true;
      }
    }

    const compatibility = evaluateTerminalSectionCompatibility({
      terminalCode,
      sections: [sum],
      terminali: terminalData,
      allowMultiplePotential: false
    });

    // I valori sono in mm²; formatSezione converte in AWG per la visualizzazione.
    if (!compatibility.isCompatible && compatibility.reason === 'below-min') {
      const sumDisplayVal = isGrouped ? sum : originalSezione;
      issues.push(getTranslation(lingua, isGrouped ? 'sectionUnderMinCombined' : 'sectionUnderMin', {
        terminal: terminalCode,
        sezione: formatSezione(sumDisplayVal, useAWG),
        min: formatSezione(config.minSection, useAWG)
      }));
      return;
    }

    if (!compatibility.isCompatible && compatibility.reason === 'above-max') {
      if (isGrouped) {
        issues.push(getTranslation(lingua, 'sectionOverMaxTotalCombined', {
          terminal: terminalCode,
          sum: formatSezione(sum, useAWG),
          max: formatSezione(config.maxSection, useAWG)
        }));
      } else {
        issues.push(getTranslation(lingua, 'sectionOverMaxSingle', {
          terminal: terminalCode,
          sezione: formatSezione(originalSezione, useAWG),
          max: formatSezione(config.maxSection, useAWG)
        }));
      }
    }
  };

  if (cable.poli && Array.isArray(cable.poli)) {
    if (!cable.nomeCavo || cable.nomeCavo.trim() === '') {
      issues.push(getTranslation(lingua, 'cableNameMissing'));
    }

    cable.poli.forEach((polo, index) => {
      const normalizedPolo = normalizeMultipolarPole(polo);

      const termAValidation = validateTerminal(normalizedPolo.terminaleA, lingua, terminalData);
      if (!termAValidation.isValid) {
        issues.push(getTranslation(lingua, 'poleError', { index: index + 1, error: `${getTranslation(lingua, 'terminalA')}: ${termAValidation.error}` }));
      }

      const termBValidation = validateTerminal(normalizedPolo.terminaleB, lingua, terminalData);
      if (!termBValidation.isValid) {
        issues.push(getTranslation(lingua, 'poleError', { index: index + 1, error: `${getTranslation(lingua, 'terminalB')}: ${termBValidation.error}` }));
      }

      const colorValidation = validateColor(normalizedPolo.colore, lingua);
      if (!colorValidation.isValid) {
        issues.push(getTranslation(lingua, 'poleError', { index: index + 1, error: `${getTranslation(lingua, 'color')}: ${colorValidation.error}` }));
      }

      if (normalizedPolo.sezione && normalizedPolo.sezione > 0) {
        validateSide({
          terminalCode: normalizedPolo.terminaleA,
          collegamento: normalizedPolo.collegamentoA,
          siglatura: normalizedPolo.siglaturaA,
          sezioneValue: normalizedPolo.sezione,
          side: 'A',
          poloIndex: index
        });
        validateSide({
          terminalCode: normalizedPolo.terminaleB,
          collegamento: normalizedPolo.collegamentoB,
          siglatura: normalizedPolo.siglaturaB,
          sezioneValue: normalizedPolo.sezione,
          side: 'B',
          poloIndex: index
        });
      }
    });
  } else {
    const termAValidation = validateTerminal(cable.terminaleA, lingua, terminalData);
    if (!termAValidation.isValid) {
      issues.push(getTranslation(lingua, 'terminalAError', { error: termAValidation.error }));
    }

    const termBValidation = validateTerminal(cable.terminaleB, lingua, terminalData);
    if (!termBValidation.isValid) {
      issues.push(getTranslation(lingua, 'terminalBError', { error: termBValidation.error }));
    }

    const colorValidation = validateColor(cable.colore, lingua);
    if (!colorValidation.isValid) {
      issues.push(getTranslation(lingua, 'colorError', { error: colorValidation.error }));
    }

    if (!cable.sezione || cable.sezione <= 0) {
      issues.push(getTranslation(lingua, 'invalidSection'));
    }

    if (cable.sezione && cable.sezione > 0) {
      const normalizedCable = normalizeStandardCable(cable);
      const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(normalizedCable);
      validateSide({
        terminalCode: cable.terminaleA,
        collegamento: cable.collegamentoA,
        siglatura: siglaturaA,
        sezioneValue: cable.sezione,
        side: 'A'
      });
      validateSide({
        terminalCode: cable.terminaleB,
        collegamento: cable.collegamentoB,
        siglatura: siglaturaB,
        sezioneValue: cable.sezione,
        side: 'B'
      });
    }

    const duplicateCheck = checkIfCableIsDuplicate(cable, cables);
    if (duplicateCheck.isDuplicate) {
      const otherNumbers = duplicateCheck.duplicateIndexes
        .filter(idx => idx !== cable.originalIndex)
        .map(idx => `#${idx + 1}`)
        .join(', ');
      issues.push(getTranslation(lingua, 'duplicateCableOf', { cables: otherNumbers }));
    }
  }

  return {
    isValid: issues.length === 0,
    issues
  };
};

export const validateCableFields = (cable, cables, lingua) => {
  const missingFields = [];

  if (cable.poli && Array.isArray(cable.poli) && cable.poli.length > 0) {
    if (!cable.nomeCavo || cable.nomeCavo.trim() === '') {
      missingFields.push('nome cavo');
    }
    cable.poli.forEach((polo, index) => {
      if (!polo.collegamentoA || polo.collegamentoA.trim() === '') {
        missingFields.push(`collegamento A per polo ${index + 1}`);
      }
      if (!polo.collegamentoB || polo.collegamentoB.trim() === '') {
        missingFields.push(`collegamento B per polo ${index + 1}`);
      }
    });
  } else {
    const normalizedCable = normalizeStandardCable(cable);
    const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(normalizedCable);
    if (!siglaturaA && !siglaturaB) {
      missingFields.push('siglatura A/B');
    }
    if (!cable.collegamentoA || cable.collegamentoA.trim() === '') {
      missingFields.push('collegamento A');
    }
    if (!cable.collegamentoB || cable.collegamentoB.trim() === '') {
      missingFields.push('collegamento B');
    }
  }
  return missingFields;
};
